import React from 'react';
import PropTypes from 'prop-types';
import './HiveComponent.scss';
import { Typography } from '@mui/material';

export const HiveComponentPossibleStatuses = {
    active: 'active',
    completed: 'completed',
    locked: 'locked'
};
export const HiveComponentPossibleSizes = {
    small: 'small',
    medium: 'medium',
    large: 'large'
};

 /**
  * It can display different states (active, completed, locked) and contains an icon and text
  * @param {object} props - The component props
  * @param {React.ReactNode} props.icon - The icon to display in the hive
  * @param {React.ReactNode} props.secondaryIcon - The secondary icon to display in the hive
  * @param {string} props.text - The text to display in the hive
  * @param {string} props.size - The size of the hive
  * @param {function} props.onClick - The function to call when the hive is clicked
  * @param {string} props.status - The status of the hive
  * @param {boolean} props.isInteractive - Whether the hive is interactive
  */
export const HiveComponent = ({
  icon,
  secondaryIcon,
  text,
  size = HiveComponentPossibleSizes.medium,
  onClick,
  status = HiveComponentPossibleStatuses.active,
  isInteractive = true,
}) => {
  // Size classes based on the size prop
  const sizeClasses = {
    small: 'hive-small',
    medium: 'hive-medium',
    large: 'hive-large',
  };

  // Status classes for different milestone states
  const statusClasses = {
    active: 'hive-active',
    completed: 'hive-completed',
    locked: 'hive-locked',
  };

  // Handle click only if component is interactive and not locked
  const handleClick = () => {
    if (isInteractive && status !== HiveComponentPossibleStatuses.locked && onClick) {
      onClick();
    }
  };
  
  // Small hives don't display icons
  const showIcon = icon && size !== HiveComponentPossibleSizes.small;
  // Helper to render icon correctly whether it's a string (image path) or React element
  const renderIcon = ({ iconElement }) => {
    // Check if icon is a string (image path)
    if (typeof iconElement === 'string') {
      return <img src={iconElement} alt="" className="hive-icon-img" />;
    }
    // Otherwise it's a React element, render as is
    return iconElement;
  };

  return (
    <div 
      className={`hive-component ${sizeClasses[size]} ${statusClasses[status]} ${isInteractive && status !== HiveComponentPossibleStatuses.locked ? 'hive-interactive' : ''}`}
      onClick={handleClick}
      role={isInteractive && status !== HiveComponentPossibleStatuses.locked ? 'button' : undefined}
      tabIndex={isInteractive && status !== HiveComponentPossibleStatuses.locked ? 0 : undefined}
      aria-disabled={status === HiveComponentPossibleStatuses.locked}
    >
      <div className="hive-content">
        {/* Secondary icon positioned in top left corner if provided and size is medium or large */}
        {secondaryIcon && (size === HiveComponentPossibleSizes.large || size === HiveComponentPossibleSizes.medium) && (
          <div className="hive-secondary-icon">
            {renderIcon({ iconElement: secondaryIcon })}
          </div>
        )}
        
        {/* Content layout based on size and available elements */}
        {showIcon ? (
          <>
            <div className="hive-icon">
              {renderIcon({ iconElement: icon })}
            </div>
            {text && (
              <Typography variant="body2" className="hive-text">
                {text}
              </Typography>
            )}
          </>
        ) : (
          // For small hives or when no icon is available
          <Typography 
            variant="body1" 
            className="hive-text-central"
          >
            {text}
          </Typography>
        )}
      </div>
    </div>
  );
};

HiveComponent.propTypes = {
  /** Icon element to display in the hive (React element) */
  icon: PropTypes.element,
  /** Secondary icon element to display in the top left corner (React element) */
  secondaryIcon: PropTypes.element,
  /** Text to display (can be string or React node for line breaks) */
  text: PropTypes.node,
  /** Size of the hive component */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Click handler function */
  onClick: PropTypes.func,
  /** Current status of the milestone */
  status: PropTypes.oneOf(['active', 'completed', 'locked']),
  /** Whether the component should be interactive */
  isInteractive: PropTypes.bool,
  /** Position of the hive in the layout (affects styling) */
  position: PropTypes.oneOf(['left', 'center', 'right']),
};
